/**
 * Admin Users Management Screen
 * Integrated users management for the main webapp
 */

import { useState, useEffect } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { api } from '../../services/api';

interface User {
  id: string;
  telegramUserId: number;
  username: string | null;
  firstName: string;
  lastName: string | null;
  balance: number;
  usdtBalance: number;
  dailyIncome: number;
  level: number;
  experience: number;
  vipStatus: boolean;
  vipExpiresAt: string | null;
  isAdmin: boolean;
  isFrozen: boolean;
  frozenReason: string | null;
  miningFrozen: boolean;
  createdAt: string;
  updatedAt: string;
}

interface UsersResponse {
  users: User[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export function AdminUsers() {
  const { t } = useTranslation();
  const [users, setUsers] = useState<User[]>([]);
  const [pagination, setPagination] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [balanceModal, setBalanceModal] = useState(false);
  const [balanceAmount, setBalanceAmount] = useState('');
  const [balanceReason, setBalanceReason] = useState('');

  useEffect(() => {
    loadUsers();
  }, [page, search]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await api.getAdminUsers({ page, search: search || undefined });
      const data: UsersResponse = response.data;
      setUsers(data.users);
      setPagination(data.pagination);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    loadUsers();
  };

  const openBalanceModal = (user: User) => {
    setSelectedUser(user);
    setBalanceModal(true);
    setBalanceAmount('');
    setBalanceReason('');
  };

  const [freezeModal, setFreezeModal] = useState(false);
  const [freezeReason, setFreezeReason] = useState('');
  const [freezeUser, setFreezeUser] = useState<User | null>(null);

  const toggleFreeze = (user: User) => {
    if (user.isFrozen) {
      if (confirm(`Unfreeze account for ${user.firstName} ${user.lastName || ''}?`)) {
        api.freezeUser(user.id, false).then(() => { loadUsers(); }).catch((err: any) => {
          alert('Failed: ' + (err.response?.data?.error?.message || err.message));
        });
      }
    } else {
      setFreezeUser(user);
      setFreezeReason('');
      setFreezeModal(true);
    }
  };

  const confirmFreeze = () => {
    if (!freezeUser || !freezeReason.trim()) {
      alert('Please enter a reason');
      return;
    }
    api.freezeUser(freezeUser.id, true, freezeReason.trim()).then(() => {
      setFreezeModal(false);
      loadUsers();
    }).catch((err: any) => {
      alert('Failed: ' + (err.response?.data?.error?.message || err.message));
    });
  };

  const toggleMiningFreeze = (user: User) => {
    const action = user.miningFrozen ? 'unfreeze' : 'freeze';
    if (confirm(`${action} mining for ${user.firstName} ${user.lastName || ''}?`)) {
      api.freezeUserMining(user.id, !user.miningFrozen).then(() => { loadUsers(); }).catch((err: any) => {
        alert('Failed: ' + (err.response?.data?.error?.message || err.message));
      });
    }
  };

  const [balanceType, setBalanceType] = useState<'witon' | 'usdt' | 'bot'>('witon');

  const modifyBalance = async () => {
    if (!selectedUser || !balanceAmount) {
      alert('Please enter an amount');
      return;
    }

    if (!balanceReason || balanceReason.trim().length === 0) {
      alert('Please enter a reason for the balance modification');
      return;
    }

    const amount = parseFloat(balanceAmount);
    if (isNaN(amount) || amount === 0) {
      alert('Please enter a valid non-zero amount');
      return;
    }

    try {
      if (balanceType === 'usdt') {
        await api.modifyUserUsdtBalance(selectedUser.id, amount, balanceReason.trim());
      } else if (balanceType === 'bot') {
        await api.modifyUserBotBalance(selectedUser.id, amount, balanceReason.trim());
      } else {
        await api.modifyUserBalance(selectedUser.id, amount, balanceReason.trim());
      }
      
      setBalanceModal(false);
      setBalanceAmount('');
      setBalanceReason('');
      alert(`${balanceType.toUpperCase()} balance updated successfully!`);
      loadUsers();
    } catch (err: any) {
      alert('Failed to update balance: ' + (err.response?.data?.error?.message || err.message || 'Unknown error'));
    }
  };

  if (loading && users.length === 0) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #0a1628 0%, #000814 100%)',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div style={{ fontSize: '18px' }}>{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #0a1628 0%, #000814 100%)',
      color: '#fff',
      paddingBottom: '80px',
    }}>
      {/* Header */}
      <div style={{
        padding: '20px',
        background: 'rgba(10, 22, 40, 0.8)',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '16px',
      }}>
        <h1 style={{
          fontSize: '24px',
          fontWeight: 700,
          margin: 0,
        }}>
          {t('admin.users')}
        </h1>
        
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: '10px' }}>
          <input
            type="text"
            placeholder="Search users..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '8px',
              padding: '8px 12px',
              color: '#fff',
              fontSize: '14px',
              width: '200px',
            }}
          />
          <button
            type="submit"
            style={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
              border: 'none',
              borderRadius: '8px',
              padding: '8px 16px',
              color: '#fff',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Search
          </button>
        </form>
      </div>

      {error && (
        <div style={{
          margin: '16px',
          padding: '16px',
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '8px',
          color: '#ef4444',
        }}>
          Error: {error}
        </div>
      )}

      {/* Users Table */}
      <div style={{
        margin: '16px',
        background: 'rgba(10, 22, 40, 0.6)',
        borderRadius: '16px',
        border: '1px solid rgba(59, 130, 246, 0.2)',
        overflow: 'hidden',
      }}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <div style={{ fontSize: '18px' }}>{t('common.loading')}</div>
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
              }}>
                <thead>
                  <tr style={{
                    background: 'rgba(59, 130, 246, 0.1)',
                    borderBottom: '1px solid rgba(59, 130, 246, 0.2)',
                  }}>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: 600 }}>User</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: 600 }}>TG ID</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: 600 }}>WiTon</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: 600 }}>USDT</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: 600 }}>Income</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: 600 }}>Status</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: 600 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} style={{
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                      background: user.isFrozen ? 'rgba(239, 68, 68, 0.05)' : 'transparent',
                    }}>
                      <td style={{ padding: '12px' }}>
                        <div>
                          <div style={{ fontWeight: 500, marginBottom: '2px', fontSize: '13px' }}>
                            {user.isFrozen && '🔒 '}{user.firstName} {user.lastName}
                          </div>
                          {user.username && (
                            <div style={{ fontSize: '11px', color: '#666' }}>@{user.username}</div>
                          )}
                          {user.isFrozen && user.frozenReason && (
                            <div style={{ fontSize: '10px', color: '#ef4444', marginTop: '2px' }}>{user.frozenReason}</div>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '12px', fontSize: '13px' }}>{user.telegramUserId}</td>
                      <td style={{ padding: '12px', fontSize: '13px' }}>{user.balance.toLocaleString()}</td>
                      <td style={{ padding: '12px', fontSize: '13px', color: '#10b981' }}>${user.usdtBalance?.toFixed(2) || '0.00'}</td>
                      <td style={{ padding: '12px', fontSize: '13px' }}>
                        {user.dailyIncome.toLocaleString()}/d
                        {user.miningFrozen && <span style={{ color: '#f59e0b', fontSize: '10px', display: 'block' }}>⛏️ frozen</span>}
                      </td>
                      <td style={{ padding: '12px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                          {user.isFrozen ? (
                            <span style={{ background: 'rgba(239, 68, 68, 0.2)', color: '#ef4444', padding: '2px 6px', borderRadius: '8px', fontSize: '11px', fontWeight: 600 }}>Frozen</span>
                          ) : user.vipStatus ? (
                            <span style={{ background: 'rgba(16, 185, 129, 0.2)', color: '#10b981', padding: '2px 6px', borderRadius: '8px', fontSize: '11px', fontWeight: 600 }}>VIP</span>
                          ) : (
                            <span style={{ background: 'rgba(255,255,255,0.1)', padding: '2px 6px', borderRadius: '8px', fontSize: '11px', fontWeight: 600 }}>Lv{user.level}</span>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <button onClick={() => openBalanceModal(user)}
                            style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)', border: 'none', borderRadius: '6px', padding: '4px 8px', color: '#fff', fontSize: '11px', fontWeight: 600, cursor: 'pointer' }}>
                            💰 Balance
                          </button>
                          <button onClick={() => toggleFreeze(user)}
                            style={{ background: user.isFrozen ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.2)', border: `1px solid ${user.isFrozen ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`, borderRadius: '6px', padding: '4px 8px', color: user.isFrozen ? '#10b981' : '#ef4444', fontSize: '11px', fontWeight: 600, cursor: 'pointer' }}>
                            {user.isFrozen ? '🔓 Unfreeze' : '🔒 Freeze'}
                          </button>
                          <button onClick={() => toggleMiningFreeze(user)}
                            style={{ background: user.miningFrozen ? 'rgba(16,185,129,0.2)' : 'rgba(245,158,11,0.2)', border: `1px solid ${user.miningFrozen ? 'rgba(16,185,129,0.3)' : 'rgba(245,158,11,0.3)'}`, borderRadius: '6px', padding: '4px 8px', color: user.miningFrozen ? '#10b981' : '#f59e0b', fontSize: '11px', fontWeight: 600, cursor: 'pointer' }}>
                            {user.miningFrozen ? '⛏️ Unfreeze Mining' : '⛏️ Freeze Mining'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination && (
              <div style={{
                padding: '20px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
              }}>
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page <= 1}
                  style={{
                    background: page <= 1 ? 'rgba(255, 255, 255, 0.1)' : 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    color: '#fff',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: page <= 1 ? 'not-allowed' : 'pointer',
                  }}
                >
                  Previous
                </button>
                
                <span style={{ fontSize: '14px' }}>
                  Page {pagination.page} of {pagination.pages} 
                  ({pagination.total} total users)
                </span>
                
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page >= pagination.pages}
                  style={{
                    background: page >= pagination.pages ? 'rgba(255, 255, 255, 0.1)' : 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    color: '#fff',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: page >= pagination.pages ? 'not-allowed' : 'pointer',
                  }}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Freeze Reason Modal */}
      {freezeModal && freezeUser && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            background: 'linear-gradient(180deg, #0a1628 0%, #000814 100%)', borderRadius: '16px', padding: '24px',
            width: '90%', maxWidth: '400px', border: '1px solid rgba(239, 68, 68, 0.3)',
          }}>
            <h3 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 16px 0' }}>
              🔒 Freeze Account — {freezeUser.firstName} {freezeUser.lastName || ''}
            </h3>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>Reason</label>
              <textarea
                value={freezeReason}
                onChange={(e) => setFreezeReason(e.target.value)}
                placeholder="Enter reason for freezing..."
                rows={3}
                style={{
                  width: '100%', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '8px', padding: '8px 12px', color: '#fff', fontSize: '14px', resize: 'vertical', boxSizing: 'border-box',
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setFreezeModal(false)}
                style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', padding: '8px 16px', color: '#fff', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={confirmFreeze}
                style={{ background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)', border: 'none', borderRadius: '8px', padding: '8px 16px', color: '#fff', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}>
                Freeze Account
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Balance Modification Modal */}
      {balanceModal && selectedUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            background: 'linear-gradient(180deg, #0a1628 0%, #000814 100%)',
            borderRadius: '16px',
            padding: '24px',
            width: '90%',
            maxWidth: '400px',
            border: '1px solid rgba(59, 130, 246, 0.3)',
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
            }}>
              <h3 style={{
                fontSize: '20px',
                fontWeight: 600,
                margin: 0,
              }}>
                Modify Balance - {selectedUser.firstName} {selectedUser.lastName}
              </h3>
              <button
                onClick={() => setBalanceModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#fff',
                  fontSize: '24px',
                  cursor: 'pointer',
                }}
              >
                ×
              </button>
            </div>
            
            <div>
              {/* Balance Type Toggle */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '16px' }}>
                <button onClick={() => setBalanceType('witon')}
                  style={{ padding: '8px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: '#fff',
                    background: balanceType === 'witon' ? 'linear-gradient(135deg, #7b1fa2 0%, #9c27b0 100%)' : 'rgba(255,255,255,0.1)' }}>
                  WiTon
                </button>
                <button onClick={() => setBalanceType('usdt')}
                  style={{ padding: '8px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: '#fff',
                    background: balanceType === 'usdt' ? 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)' : 'rgba(255,255,255,0.1)' }}>
                  USDT
                </button>
                <button onClick={() => setBalanceType('bot')}
                  style={{ padding: '8px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: '#fff',
                    background: balanceType === 'bot' ? 'linear-gradient(135deg, #e11d48 0%, #f43f5e 100%)' : 'rgba(255,255,255,0.1)' }}>
                  Bot (AI)
                </button>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 600,
                  marginBottom: '8px',
                }}>
                  Current Balance
                </label>
                <div style={{
                  padding: '8px 12px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  fontSize: '14px',
                }}>
                  {selectedUser.balance.toLocaleString()} WiTon
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 600,
                  marginBottom: '8px',
                }}>
                  Amount (can be negative)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={balanceAmount}
                  onChange={(e) => setBalanceAmount(e.target.value)}
                  placeholder="e.g., 1000 or -500"
                  style={{
                    width: '100%',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '8px',
                    padding: '8px 12px',
                    color: '#fff',
                    fontSize: '14px',
                  }}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 600,
                  marginBottom: '8px',
                }}>
                  Reason
                </label>
                <input
                  type="text"
                  value={balanceReason}
                  onChange={(e) => setBalanceReason(e.target.value)}
                  placeholder="Reason for balance modification"
                  style={{
                    width: '100%',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '8px',
                    padding: '8px 12px',
                    color: '#fff',
                    fontSize: '14px',
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setBalanceModal(false)}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    color: '#fff',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={modifyBalance}
                  style={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    color: '#fff',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Update Balance
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}